import { useRef, useEffect } from 'react';

import { OrbitControls } from 'three-stdlib';

export type UseTwoFingerGestureClassificationParams = {
    controls: OrbitControls;
    canvas: HTMLElement;
    enablePan: boolean;
};

const CLASSIFICATION_THRESHOLD = 2;
const MIN_MOVEMENT = 16;
const UNDETERMINED = 'undetermined';
const ZOOM = 'zoom';
const PAN = 'pan';

type Mode = typeof UNDETERMINED | typeof ZOOM | typeof PAN;

// Two-finger gesture classification: distinguish pan vs zoom
// On gesture start, we wait to see if the user is pinching (zoom) or dragging (pan)
// Then lock into that mode for the duration of the gesture
export const useTwoFingerGestureClassification = ({
    controls,
    canvas,
    enablePan,
}: UseTwoFingerGestureClassificationParams) => {
    const gestureRef = useRef<{
        mode: Mode;
        initialDistance: number;
        initialMidpoint: { x: number; y: number };
    }>({
        mode: UNDETERMINED,
        initialDistance: 0,
        initialMidpoint: { x: 0, y: 0 },
    });

    useEffect(() => {
        if (!enablePan) return;

        const getDistance = (t1: Touch, t2: Touch) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

        const getMidpoint = (t1: Touch, t2: Touch) => ({
            x: (t1.clientX + t2.clientX) / 2,
            y: (t1.clientY + t2.clientY) / 2,
        });

        const handleTouchStart = (e: TouchEvent) => {
            if (e.touches.length !== 2) return;

            gestureRef.current = {
                mode: UNDETERMINED,
                initialDistance: getDistance(e.touches[0], e.touches[1]),
                initialMidpoint: getMidpoint(e.touches[0], e.touches[1]),
            };

            controls.enableZoom = true;
            controls.enablePan = true;
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (e.touches.length !== 2) return;

            const gesture = gestureRef.current;
            if (gesture.mode !== UNDETERMINED) return;

            const currentDistance = getDistance(e.touches[0], e.touches[1]);
            const currentMidpoint = getMidpoint(e.touches[0], e.touches[1]);

            const pinchDelta = Math.abs(currentDistance - gesture.initialDistance);
            const panDelta = Math.hypot(
                currentMidpoint.x - gesture.initialMidpoint.x,
                currentMidpoint.y - gesture.initialMidpoint.y,
            );

            if (pinchDelta < MIN_MOVEMENT && panDelta < MIN_MOVEMENT) return;

            if (pinchDelta > panDelta * CLASSIFICATION_THRESHOLD) {
                gesture.mode = ZOOM;
                controls.enablePan = false;
            } else {
                gesture.mode = PAN;
                controls.enableZoom = false;
            }
        };

        const handleTouchEnd = () => {
            gestureRef.current.mode = UNDETERMINED;
            controls.enableZoom = true;
            controls.enablePan = enablePan;
        };

        canvas.addEventListener('touchstart', handleTouchStart, { passive: true });
        canvas.addEventListener('touchmove', handleTouchMove, { passive: true });
        canvas.addEventListener('touchend', handleTouchEnd);
        canvas.addEventListener('touchcancel', handleTouchEnd);

        return () => {
            canvas.removeEventListener('touchstart', handleTouchStart);
            canvas.removeEventListener('touchmove', handleTouchMove);
            canvas.removeEventListener('touchend', handleTouchEnd);
            canvas.removeEventListener('touchcancel', handleTouchEnd);
        };
    }, [canvas, controls, enablePan]);
};
